import type { BundleTier } from './bundle-types';

export interface BundlePreview {
	quantity: number;
	subtotal: number;
	currencyCode: string;
	currentTier: BundleTier | null;
	nextTier: BundleTier | null;
	/** Display-only percentage from the active tier (0 when none reached). */
	percentOff: number;
	/** Projected savings = subtotal * percentOff (display only). */
	discountAmount: number;
	/** Projected total after the projected discount. */
	total: number;
	/** Tees still needed to reach `nextTier` (0 when maxed). */
	remainingToNext: number;
	/** 0–100 toward the next tier (100 when the top tier is reached). */
	progressPercent: number;
}

function round2(amount: number): number {
	return Math.round(amount * 100) / 100;
}

/**
 * Pure, display-only bundle math. The Shopify automatic discount is the source
 * of truth at checkout; this drives the "estimated" panel + sticky-bar copy.
 */
export function computeBundlePreview(
	quantity: number,
	subtotal: number,
	currencyCode: string,
	tiers: BundleTier[],
): BundlePreview {
	const sorted = [...tiers].sort((a, b) => a.minQuantity - b.minQuantity);

	let currentTier: BundleTier | null = null;
	let nextTier: BundleTier | null = null;
	for (const tier of sorted) {
		if (quantity >= tier.minQuantity) {
			currentTier = tier;
		} else if (!nextTier) {
			nextTier = tier;
		}
	}

	const percentOff = currentTier?.percentageDiscount ?? 0;
	const discountAmount = round2((subtotal * percentOff) / 100);
	const total = round2(subtotal - discountAmount);

	const remainingToNext = nextTier ? Math.max(0, nextTier.minQuantity - quantity) : 0;
	const progressPercent = nextTier
		? Math.min(100, (quantity / nextTier.minQuantity) * 100)
		: quantity > 0
			? 100
			: 0;

	return {
		quantity,
		subtotal: round2(subtotal),
		currencyCode,
		currentTier,
		nextTier,
		percentOff,
		discountAmount,
		total,
		remainingToNext,
		progressPercent,
	};
}
